// SPDX-License-Identifier: AGPL-3.0-or-later

export interface OrgThemeSettings {
  brand_color?: string;
  brand_radius?: 'none' | 'sm' | 'md' | 'lg' | 'full';
  brand_name?: string;
}

function hexToRgb(hex: string) {
  const cleanHex = hex.replace('#', '');
  if (cleanHex.length !== 6) return null;
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  return { r, g, b };
}

function lightenDarkenColor(r: number, g: number, b: number, percent: number) {
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, Math.min(255, r + amt));
  const G = Math.max(0, Math.min(255, g + amt));
  const B = Math.max(0, Math.min(255, b + amt));
  return { r: R, g: G, b: B };
}

export function OrgThemeStyles({ settings }: { settings?: OrgThemeSettings | null | undefined }) {
  if (!settings) return null;

  const { brand_color, brand_radius } = settings;
  if (!brand_color && !brand_radius) return null;

  let cssContent = '';

  if (brand_color) {
    const rgb = hexToRgb(brand_color);
    if (rgb) {
      const triplet = `${rgb.r} ${rgb.g} ${rgb.b}`;

      // Calculate hover (darken by 12% for visual click feedback)
      const hover = lightenDarkenColor(rgb.r, rgb.g, rgb.b, -12);
      const hoverTriplet = `${hover.r} ${hover.g} ${hover.b}`;

      // Calculate subtle (lighten by 85% for primary subtle tint)
      const subtle = lightenDarkenColor(rgb.r, rgb.g, rgb.b, 85);
      const subtleTriplet = `${subtle.r} ${subtle.g} ${subtle.b}`;

      cssContent += `
        --color-primary: ${triplet};
        --color-primary-hover: ${hoverTriplet};
        --color-primary-subtle: ${subtleTriplet};
        --color-primary-token: ${triplet};
      `;
    }
  }

  if (brand_radius) {
    let sm = '4px',
      md = '6px',
      lg = '8px',
      xl = '12px';
    if (brand_radius === 'none') {
      sm = '0px';
      md = '0px';
      lg = '0px';
      xl = '0px';
    } else if (brand_radius === 'sm') {
      sm = '4px';
      md = '6px';
      lg = '8px';
      xl = '12px';
    } else if (brand_radius === 'md') {
      sm = '8px';
      md = '12px';
      lg = '16px';
      xl = '24px';
    } else if (brand_radius === 'lg') {
      sm = '12px';
      md = '18px';
      lg = '24px';
      xl = '36px';
    } else if (brand_radius === 'full') {
      sm = '9999px';
      md = '9999px';
      lg = '9999px';
      xl = '9999px';
    }

    cssContent += `
      --radius-sm: ${sm};
      --radius-md: ${md};
      --radius-lg: ${lg};
      --radius-xl: ${xl};
    `;
  }

  return (
    <style>
      {`
        :root {
          ${cssContent}
        }
      `}
    </style>
  );
}
